import { useEffect, useRef, useState } from 'react';
import { i18n } from '@lingui/core';
import { I18nProvider } from '@lingui/react';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { Banner, HelpIcon, LoadingIndicator } from '@thermon-platforms/thermon-ui-core';
import useDevicesStore from 'store/devices';
import api, { parseErrors } from 'util/api';


function Modal({device, close}) {
  const [devicesStore] = useDevicesStore();
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState();
  const [mappingError, setMappingError] = useState();
  const [cards, setCards] = useState([]);
  const [mappings, setMappings] = useState([]);
  const [addedCard, setAddedCard] = useState('');
  const [removedCard, setRemovedCard] = useState('');

  const queryInterval = useRef();
  useEffect(() => {
    queryCards();
    queryInterval.current = setInterval(() => {
      queryCards();
    }, 30 * 1000);

    return () => clearInterval(queryInterval.current);
  }, []);

  function queryCards() {
    if (!mappings || mappings.length == 0) setLoading(true);
    api(`/devices/${device.model.id}/getCircuitCardMappings`)
      .then(async res => {
        const data = await res.json();
        setCards(data.installedDevices);
        setMappings(data.t2000SerialNumberMappings);
      })
      .catch(() => {/* no-op */})
      .finally(() => {
        setLoading(false);
      });
  }

  function addCard() {
    if (!addedCard) return;
    setProcessing(true);
    setError(null);
    api(`/devices/${device.model.id}/add-card`, {
      method: 'POST',
      body: JSON.stringify(addedCard)
    })
      .then(() => {
        setSuccess(true);
        setTimeout(() => setSuccess(false), 10 * 1000);
        setAddedCard('');
        queryCards();
      })
      .catch(async res => {
        const data = await res.json();
        setError(parseErrors(data));
      })
      .finally(() => {
        setProcessing(false);
      });
  }

  function removeCard() {
    if (!removedCard) return;
    setProcessing(true);
    setError(null);
    api(`/devices/${device.model.id}/remove-card`, {
      method: 'DELETE',
      body: JSON.stringify(removedCard)
    })
      .then(() => {
        setSuccess(true);
        setTimeout(() => setSuccess(false), 10 * 1000);
        setRemovedCard('');
        queryCards();
      })
      .catch(async res => {
        const data = await res.json();
        setError(parseErrors(data));
      })
      .finally(() => {
        setProcessing(false);
      });
  }

  function updateMapping(circuit, serialNumber) {
    setProcessing(true);
    setMappingError(undefined);

    api(`/devices/${circuit.id}`, {
      method: 'PATCH',
      body: JSON.stringify({
        type: 'Circuit',
        configuration: [
          {
            key: 'SerialNumber',
            value: serialNumber,
          }
        ]
      })
    })
      .then(() => {
        setSuccess(true);
        setTimeout(() => setSuccess(false), 10 * 1000);
        queryCards();
      })
      .catch(async res => {
        const data = await res.json();
        setMappingError(parseErrors(data));
      })
      .finally(() => {
        setProcessing(false);
      });
  }

  return (
    <I18nProvider i18n={i18n}>
      <header>
        <h3><Trans>Card management for</Trans>: <strong>{device.model.name}</strong></h3>
      </header>
      <section>
        <Banner status="ok" show={success}>
          <Trans>Card updates have been requested. Some changes may take a moment to take effect.</Trans>
        </Banner>
        <Banner status="alert" show={error}>{JSON.stringify(error)}</Banner>
        {loading && !cards.length ? <LoadingIndicator /> : <>
          <ul className="form">
            <li>
              <label htmlFor="add-card">
                <Trans>Card to Add</Trans>
                <HelpIcon text={t`Input the serial number found on the new card`} />
              </label>
              <input
                id="add-card"
                name="add-card"
                type="number"
                width={6}
                value={addedCard}
                onChange={e => setAddedCard(e.target.value)}
              />
              <button
                onClick={addCard}
                className={processing ? 'processing' : ''}
                disabled={!addedCard || (processing && addedCard)}
              >
                <Trans>Add Card</Trans>
              </button>
            </li>
            <li>
              <label htmlFor="remove-card">
                <Trans>Card to Remove</Trans>
                <HelpIcon text={t`To remove a card, it must not be currently mapped to a circuit.`} />
              </label>
              <select
                id="remove-card"
                name="remove-card"
                disabled={loading}
                value={removedCard}
                onChange={e => setRemovedCard(e.target.value)}
              >
                <option value="">- <Trans>Select a card</Trans> -</option>
                {cards?.map(card => {
                  if (card == '0') return null;
                  // If the card is already mapped, we cannot remove it
                  if (mappings.find(mapping =>
                    mapping.currentSerialNumber == card
                  )) return null;

                  return (
                    <option key={`remove-card-option-${card}`} value={card}>
                      {card}
                    </option>
                  );
                })}
              </select>
              <button
                onClick={removeCard}
                className={processing ? 'processing' : ''}
                disabled={!removedCard || (processing && removedCard)}
              >
                <Trans>Remove Card</Trans>
              </button>
            </li>
          </ul>
        </>}
        <hr />
        <h3><Trans>Circuit Mapping</Trans></h3>
        <Banner status="alert" show={mappingError}>{JSON.stringify(mappingError)}</Banner>
        <div className="table-container">
          <table className="circuit-mapping-table">
            <thead>
              <tr>
                <th></th>
                <th>
                  <Trans>Circuit</Trans>
                </th>
                <th>
                  <Trans>Card Serial Number</Trans>
                </th>
              </tr>
            </thead>
            <tbody>
              {device.model.children.map((circuit, idx) => {
                return (
                  <tr key={`circuit-mapping-row-${idx}`}>
                    <td>{idx + 1}</td>
                    <td>{circuit.name}</td>
                    <td>
                      <select
                        disabled={loading || processing}
                        onChange={event => updateMapping(circuit, event.target.value)}
                        value={
                          (() => {
                            const mapping = mappings.find(mapping => mapping.circuitId == circuit.id);
                            return mapping ? mapping.currentSerialNumber : '0';
                          })()
                        }
                      >
                        <option value="0"><Trans>None</Trans></option>
                        {cards?.map(card => {
                          // We handle the "None" option above
                          if (card == '0') return null;
                          // If the card is already mapped to another circuit, skip it
                          if (mappings.find(mapping =>
                            mapping.circuitId != circuit.id && mapping.currentSerialNumber == card
                          )) return null;

                          return (<option
                            key={`circuit-mapping-card-option-${card}`}
                            value={card}
                          >
                            {card}
                          </option>);
                        })}
                      </select>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </section>
      <footer>
        <button disabled={processing} onClick={close}>
          <Trans>Close</Trans>
        </button>
      </footer>
    </I18nProvider>
  )
}

export default Modal;
